import React, { FunctionComponent, useState, useCallback } from 'react';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import WithDataRecord from 'hoc/WithDataRecord';
import { AdminApi, AdminInvestorsListOrderingEnum } from 'api/apis';
import { AdminInvestorList, DistributionPlatformPermissionEnum } from 'api/models';
import StudioInvestorsTable from './studio-investors-table';
import useGoTo from 'hooks/use-go-to';
import WideContent from 'core/layout/content/wide-content';
import { ISSUER_ROUTES } from 'apps/issuer/pages/routes.config';
import AddInvestorModal from './add-investor-modal';
import Button from 'ui/atoms/button';
import { useCurrentUserSelector } from 'core/auth/hooks';
import { hasDistributionViewDataPermission } from '../../helpers/distributionPlatformPermissions';
import { hasViewPermission } from '../../helpers/tokenPermissions';
import { useServerConfigSelector } from 'src/core/config/hooks';
import Spacer from 'src/ui/atoms/spacer';
import PageWrapper from 'src/core/layout/content/page-wrapper';

interface InvestorsTableProps {
  defaultLimit: number;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  setFilterPopupHeight?: (height: number) => void;
}

const IssuerInvestorsDataRecordTable: FunctionComponent<InvestorsTableProps> = WithDataRecord<
  AdminApi,
  AdminInvestorList
>(AdminApi)(
  ({ data, ordering, onOrderBy, loading, setFilterPopupHeight, paginationProps }) => {
    const goTo = useGoTo(ISSUER_ROUTES.investorDetails);
    const { currentUser } = useCurrentUserSelector();
    const { config } = useServerConfigSelector();

    const hasPermission =
      hasDistributionViewDataPermission(currentUser?.associatedDistributionPlatforms || []) ||
      hasViewPermission(currentUser) ||
      !!config.features.canRegisterOnly;

    return (
      <>
        <StudioInvestorsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          investors={data}
          loading={loading}
          goToDetails={(investorId: string) => goTo({ investorId })}
          paginationProps={paginationProps}
          hasPermission={hasPermission}
          setFilterPopupHeight={setFilterPopupHeight}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminInvestorsList({
      offset,
      limit,
      ordering: ordering as AdminInvestorsListOrderingEnum,
      ...props,
    });
  },
);

const IssuerInvestorsPage: FunctionComponent<{}> = () => {
  const [isAddInvestorModalOpen, setAddInvestorModalOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  const { currentUser } = useCurrentUserSelector();

  const refreshTableData = useCallback(() => {
    setForceDataLoad(true);
    setAddInvestorModalOpen(false);
  }, []);

  const distributionPlatformsWithPermission = currentUser?.associatedDistributionPlatforms?.filter(
    (platform) =>
      !!platform.permissions.find((perm) => perm === DistributionPlatformPermissionEnum.CREATE_INVESTOR_AND_INVESTMENT),
  );

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboardIssuerInvestors.title" />} titleSpacing="larger">
        {!!distributionPlatformsWithPermission?.length && (
          <>
            <Button
              variant="primary"
              icon="plus"
              size="small"
              iconSize="small"
              iconColor="inherit"
              onClick={() => setAddInvestorModalOpen(true)}
            >
              <Translate name="addInvestor.title" />
            </Button>
            <Spacer y={4} />
          </>
        )}
        <IssuerInvestorsDataRecordTable
          defaultLimit={50}
          forceDataLoad={forceDataLoad}
          setForceDataLoad={setForceDataLoad}
          setFilterPopupHeight={setFilterPopupHeight}
        />
        {isAddInvestorModalOpen && !!distributionPlatformsWithPermission?.length && (
          <AddInvestorModal
            onClose={() => setAddInvestorModalOpen(false)}
            onAddNewInvestor={refreshTableData}
            distributionPlatforms={distributionPlatformsWithPermission}
          />
        )}
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(IssuerInvestorsPage, AppType.ISSUER);
